import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import PageHeader from "@/components/common/PageHeader";
import { supportService } from "@/services/support.service";

const STATUS_OPTIONS = ["OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED"] as const;
const PRIORITY_OPTIONS = ["LOW", "MEDIUM", "HIGH", "URGENT"] as const;

function formatDateTime(value?: string | null) {
  return value ? new Date(value).toLocaleString() : "—";
}

function getSlaTone(status?: string) {
  switch (status) {
    case "escalated":
      return "bg-red-50 text-red-700 border-red-200";
    case "breached":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "due_soon":
      return "bg-blue-50 text-blue-700 border-blue-200";
    default:
      return "bg-green-50 text-green-700 border-green-200";
  }
}

function TicketDetailPage() {
  const params = useParams();
  const ticketId = params.id ?? "";
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-support-ticket", ticketId],
    queryFn: () => supportService.getTicket(ticketId),
    enabled: Boolean(ticketId),
  });

  const { data: assignees } = useQuery({
    queryKey: ["admin-support-assignees"],
    queryFn: () => supportService.listAssignees(),
  });

  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [assignee, setAssignee] = useState("");
  const [message, setMessage] = useState("");

  const { mutateAsync: updateTicket, isPending: isUpdating } = useMutation({
    mutationFn: () =>
      supportService.updateTicket(ticketId, {
        status: status || data?.status,
        priority: priority || data?.priority,
        assignedToId: assignee || data?.assignedTo?.id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-support-ticket", ticketId],
      });
      toast.success("Ticket updated");
    },
  });

  const { mutateAsync: sendMessage, isPending: isSending } = useMutation({
    mutationFn: () => supportService.addMessage(ticketId, { message }),
    onSuccess: () => {
      setMessage("");
      queryClient.invalidateQueries({
        queryKey: ["admin-support-ticket", ticketId],
      });
      toast.success("Message sent");
    },
  });

  const summary = useMemo(() => {
    if (!data) {
      return [];
    }
    return [
      { label: "Ticket", value: data.ticketNumber },
      { label: "Status", value: data.status },
      { label: "Priority", value: data.priority },
      { label: "Tenant", value: data.tenant?.name ?? "External" },
    ];
  }, [data]);

  if (isLoading) {
    return <div className="text-sm text-slate-500">Loading ticket...</div>;
  }

  if (!data) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-slate-500">Ticket not found.</p>
        <Link className="text-sm font-semibold text-slate-900" to="/support">
          Back to support
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title={data.subject}
        subtitle={data.reportedByEmail}
        actions={
          <Link
            to="/support"
            className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-50"
          >
            Back to support
          </Link>
        }
      />

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {summary.map((item) => (
          <div
            key={item.label}
            className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
              {item.label}
            </p>
            <p className="mt-3 text-lg font-semibold text-slate-900">
              {item.value}
            </p>
          </div>
        ))}
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
            Ticket details
          </h3>
          <p className="mt-4 text-sm text-slate-600">{data.description}</p>

          <div className="mt-6 rounded-xl border border-slate-100 p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                SLA status
              </p>
              <span
                className={`rounded-full border px-2 py-1 text-xs font-semibold ${getSlaTone(data.slaStatus)}`}
              >
                {data.slaStatus || "on_track"}
              </span>
            </div>
            <div className="mt-3 space-y-2 text-sm text-slate-600">
              <p>
                <span className="font-semibold text-slate-900">First response due:</span>{" "}
                {formatDateTime(data.firstResponseDueAt)}
              </p>
              <p>
                <span className="font-semibold text-slate-900">First responded:</span>{" "}
                {formatDateTime(data.firstRespondedAt)}
              </p>
              <p>
                <span className="font-semibold text-slate-900">Resolution due:</span>{" "}
                {formatDateTime(data.resolutionDueAt)}
              </p>
              <p>
                <span className="font-semibold text-slate-900">Escalation level:</span>{" "}
                {data.escalationLevel}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
            Manage ticket
          </h3>
          <div className="mt-4 space-y-3 text-sm text-slate-600">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Status
              </label>
              <select
                value={status || data.status}
                onChange={(event) => setStatus(event.target.value)}
                className="mt-2 h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Priority
              </label>
              <select
                value={priority || data.priority}
                onChange={(event) => setPriority(event.target.value)}
                className="mt-2 h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm"
              >
                {PRIORITY_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                Assignee
              </label>
              <select
                value={assignee || data.assignedTo?.id || ""}
                onChange={(event) => setAssignee(event.target.value)}
                className="mt-2 h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm"
              >
                <option value="">Unassigned</option>
                {assignees?.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.firstName} {person.lastName ?? ""} · {person.role}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              disabled={isUpdating}
              onClick={() => updateTicket()}
              className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white"
            >
              {isUpdating ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
          Conversation
        </h3>
        <div className="mt-4 space-y-3">
          {data.messages.length ? (
            data.messages.map((messageItem) => (
              <div
                key={messageItem.id}
                className="rounded-xl border border-slate-100 px-4 py-3 text-sm text-slate-600"
              >
                <p className="text-xs text-slate-400">
                  {messageItem.senderEmail} ·{" "}
                  {new Date(messageItem.createdAt).toLocaleString()}
                </p>
                <p className="mt-2 text-slate-700">{messageItem.message}</p>
              </div>
            ))
          ) : (
            <p className="text-sm text-slate-500">No messages yet.</p>
          )}
        </div>

        <div className="mt-6 space-y-3">
          <textarea
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            rows={4}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm"
            placeholder="Write a reply..."
          />
          <button
            type="button"
            disabled={!message.trim() || isSending}
            onClick={() => sendMessage()}
            className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white"
          >
            {isSending ? "Sending..." : "Send reply"}
          </button>
        </div>
      </section>
    </div>
  );
}

export default TicketDetailPage;
